import React, { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Divider,
  Link,
  Chip,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  ArrowForward,
  OpenInNew,
  WarningAmber,
  CheckCircleOutline,
  InfoOutlined,
  ArrowBack,
  AccessTime,
  CalendarToday,
  Person,
  LinkedIn,
} from '@mui/icons-material';
import ArticleTable from '../../components/Blog/ArticleTable';
import SEOHead from '../../components/SEO/SEOHead';
import BreadcrumbNav from '../../components/SEO/BreadcrumbNav';
import AtomicAnswer from '../../components/SEO/AtomicAnswer';
import FAQSection from '../../components/SEO/FAQSection';
import ReferralCTA from '../../components/shared/ReferralCTA';
import NewsletterSection from '../../components/Layout/NewsletterSection';
import TrustStrip from '../../components/Layout/TrustStrip';
import RelatedArticles from '../../components/Blog/RelatedArticles';
import carlosPhoto from '../../assets/img/author-carlos.jpg';
import { useTranslation } from 'react-i18next';

const CuentasSinFeesPage = () => {
  const { t, i18n } = useTranslation();
  const { lng } = useParams();
  const [takeawaysOpen, setTakeawaysOpen] = useState(false);

  const p = 'blog.cuentasSinFees';

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: t(`${p}.seoTitle`),
    description: t(`${p}.seoDescription`),
    datePublished: '2026-02-24',
    dateModified: '2026-02-24',
    author: { '@type': 'Organization', name: 'FinMaple', url: 'https://www.finmaple.com' },
    publisher: {
      '@type': 'Organization',
      name: 'FinMaple',
      url: 'https://www.finmaple.com',
      logo: { '@type': 'ImageObject', url: 'https://www.finmaple.com/og-image.jpg' },
    },
    mainEntityOfPage: 'https://www.finmaple.com/blog/mejores-cuentas-sin-fees-recien-llegados',
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['.article-headline', '.atomic-answer'],
    },
    inLanguage: i18n.language,
  };

  const HERO_IMAGE = '/images/blog/cuentas-bancarias-sin-fees-canada.jpg';
  const TAGS = [t(`${p}.tagBanca`), t(`${p}.tagNewcomers`)];
  const AUTHOR = { name: 'Carlos Alviz', role: t(`${p}.authorRole`), linkedin: 'https://www.linkedin.com/in/alvizcarlos/', photo: carlosPhoto };

  const BANCOS_DIGITALES = [
    {
      banco: 'Tangerine',
      fee: '$0',
      eTransfers: t(`${p}.unlimited`),
      interes: '0.10%',
      quebec: t(`${p}.yes`),
    },
    {
      banco: 'Simplii Financial',
      fee: '$0',
      eTransfers: t(`${p}.unlimited`),
      interes: '0.01%',
      quebec: t(`${p}.no`),
    },
    {
      banco: 'EQ Bank',
      fee: '$0',
      eTransfers: t(`${p}.unlimited`),
      interes: t(`${p}.eqInterest`),
      quebec: t(`${p}.yes`),
    },
    {
      banco: 'Wealthsimple',
      fee: '$0',
      eTransfers: t(`${p}.unlimited`),
      interes: t(`${p}.wsInterest`),
      quebec: t(`${p}.yes`),
    },
  ];

  const NEWCOMER_PAQUETES = [
    {
      banco: 'RBC',
      periodo: t(`${p}.months12`),
      feeDespues: '$4.00 - $16.95/' + t(`${p}.month`),
      sucursales: '1,200+',
      bono: t(`${p}.yesVaries`),
    },
    {
      banco: 'TD',
      periodo: t(`${p}.months12`),
      feeDespues: '$4.95 - $16.95/' + t(`${p}.month`),
      sucursales: '1,100+',
      bono: t(`${p}.yesVaries`),
    },
    {
      banco: 'CIBC',
      periodo: t(`${p}.months24`),
      feeDespues: '$4.95 - $16.95/' + t(`${p}.month`),
      sucursales: '1,000+',
      bono: t(`${p}.yesVaries`),
    },
    {
      banco: 'Scotiabank StartRight',
      periodo: t(`${p}.months12`),
      feeDespues: '$3.95 - $16.95/' + t(`${p}.month`),
      sucursales: '900+',
      bono: t(`${p}.yesVaries`),
    },
  ];

  const FAQS = [
    {
      question: t(`${p}.faq1Q`),
      answer: t(`${p}.faq1A`),
    },
    {
      question: t(`${p}.faq2Q`),
      answer: t(`${p}.faq2A`),
    },
    {
      question: t(`${p}.faq3Q`),
      answer: t(`${p}.faq3A`),
    },
    {
      question: t(`${p}.faq4Q`),
      answer: t(`${p}.faq4A`),
    },
    {
      question: t(`${p}.faq5Q`),
      answer: t(`${p}.faq5A`),
    },
  ];

  const CHECKLIST_STEPS = [
    { step: 1, title: t(`${p}.check1Title`), desc: t(`${p}.check1Desc`) },
    { step: 2, title: t(`${p}.check2Title`), desc: t(`${p}.check2Desc`) },
    { step: 3, title: t(`${p}.check3Title`), desc: t(`${p}.check3Desc`) },
    { step: 4, title: t(`${p}.check4Title`), desc: t(`${p}.check4Desc`) },
    { step: 5, title: t(`${p}.check5Title`), desc: t(`${p}.check5Desc`) },
    { step: 6, title: t(`${p}.check6Title`), desc: t(`${p}.check6Desc`) },
    { step: 7, title: t(`${p}.check7Title`), desc: t(`${p}.check7Desc`) },
  ];

  const RED_FLAGS = [
    { title: t(`${p}.rf1Title`), desc: t(`${p}.rf1Desc`) },
    { title: t(`${p}.rf2Title`), desc: t(`${p}.rf2Desc`) },
    { title: t(`${p}.rf3Title`), desc: t(`${p}.rf3Desc`) },
    { title: t(`${p}.rf4Title`), desc: t(`${p}.rf4Desc`) },
    { title: t(`${p}.rf5Title`), desc: t(`${p}.rf5Desc`) },
    { title: t(`${p}.rf6Title`), desc: t(`${p}.rf6Desc`) },
  ];

  return (
    <Box component="article" sx={{ bgcolor: '#F7F9FC' }}>
      <SEOHead
        title={t(`${p}.seoTitle`)}
        description={t(`${p}.seoDescription`)}
        canonical="/blog/mejores-cuentas-sin-fees-recien-llegados"
        routeKey="blogCuentas"
        seoKey="blogCuentas"
        noIndex={i18n.language !== 'es'}
        ogImage="https://www.finmaple.com/images/blog/cuentas-bancarias-sin-fees-canada.jpg"
        schema={articleSchema}
      />

      {/* Hero Image */}
      <Box
        role="img"
        aria-label={t(`${p}.heroAlt`)}
        sx={{
          width: '100%',
          height: { xs: 220, md: 360 },
          backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.45)), url(${HERO_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'flex-end',
        }}
      >
        <Container maxWidth="md" sx={{ pb: 4 }}>
          <Button
            component={RouterLink}
            to={`/${lng}/blog`}
            startIcon={<ArrowBack />}
            sx={{ color: '#fff', textTransform: 'none', mb: 2, fontWeight: 500 }}
          >
            {t(`${p}.backToGuides`)}
          </Button>
        </Container>
      </Box>

      <TrustStrip />

      <Container maxWidth="md" sx={{ mt: 0, position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '14px',
            px: { xs: 3, md: 5 },
            py: { xs: 3, md: 4 },
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          }}
        >
          <BreadcrumbNav items={[{ label: t(`${p}.breadcrumbGuides`), to: `/${lng}/blog` }, { label: t(`${p}.breadcrumbCuentas`) }]} />
          {/* Tags */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {TAGS.map((tag, i) => (
              <Chip key={i} label={tag} size="small" sx={{ bgcolor: '#F0F2F5', color: '#5F6B7A', fontWeight: 500, fontSize: '0.8rem' }} />
            ))}
          </Box>

          {/* Title */}
          <Typography variant="h3" component="h1" sx={{ fontWeight: 600, color: '#1A1A1A', lineHeight: 1.2, mb: 3, fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
            {t(`${p}.title`)}
          </Typography>

          {/* Meta */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, color: '#5F6B7A', mb: 3, fontSize: '0.9rem' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Person sx={{ fontSize: 18 }} />
              <a href={AUTHOR.linkedin} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#1E3A5F', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E3A5F' }}>{AUTHOR.name}</Typography>
                <LinkedIn sx={{ fontSize: 16, color: '#1E3A5F' }} />
              </a>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarToday sx={{ fontSize: 16 }} />
              <Typography variant="body2">{t(`${p}.publishDate`)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTime sx={{ fontSize: 16 }} />
              <Typography variant="body2">{t(`${p}.readTime`)}</Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Atomic Answer */}
          <AtomicAnswer text={t(`${p}.atomicAnswer`)} />

          {/* Key Takeaways - Collapsible */}
          <Box
            sx={{
              border: '1px solid #EEF0F3',
              borderRadius: '10px',
              overflow: 'hidden',
              mt: 5,
              mb: 5,
            }}
          >
            <Box
              onClick={() => setTakeawaysOpen(!takeawaysOpen)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                py: 1.5,
                bgcolor: '#F7F9FC',
                cursor: 'pointer',
                '&:hover': { bgcolor: '#EEEEEE' },
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1A1A1A' }}>
                {t(`${p}.keyTakeaways`)}
              </Typography>
              {takeawaysOpen ? <ExpandLess sx={{ color: '#5F6B7A' }} /> : <ExpandMore sx={{ color: '#5F6B7A' }} />}
            </Box>
            <Box component="ul" sx={{ display: takeawaysOpen ? 'block' : 'none', px: 3, py: 2, m: 0, '& li': { mb: 1, color: '#1A1A1A', lineHeight: 1.6 } }}>
                <li>{t(`${p}.takeaway1`)}</li>
                <li dangerouslySetInnerHTML={{ __html: t(`${p}.takeaway2`) }} />
                <li>{t(`${p}.takeaway3`)}</li>
                <li>{t(`${p}.takeaway4`)}</li>
                <li>{t(`${p}.takeaway5`)}</li>
              </Box>
          </Box>

          {/* ============================================ */}
          {/* H2: Qué significa "sin fees" en Canadá */}
          {/* ============================================ */}
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 6, mb: 3 }}>
            {t(`${p}.h2WhatMeans`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }} dangerouslySetInnerHTML={{ __html: t(`${p}.whatMeansP1`) }} />
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }} dangerouslySetInnerHTML={{ __html: t(`${p}.whatMeansP2`) }} />
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }} dangerouslySetInnerHTML={{ __html: t(`${p}.whatMeansP3`) }} />

          {/* Warning box */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              bgcolor: '#FFFBF0',
              border: '1px solid #FFE8A0',
              borderRadius: '10px',
              p: 3,
              mt: 3,
              mb: 3,
            }}
          >
            <WarningAmber sx={{ color: '#F9A825', mt: 0.3, flexShrink: 0 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 0.5 }}>
                {t(`${p}.warningTitle`)}
              </Typography>
              <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: t(`${p}.warningText`) }} />
            </Box>
          </Box>

          <Divider sx={{ mt: 5, mb: 0 }} />

          {/* ============================================ */}
          {/* H2: Opción A: Bancos 100% digitales */}
          {/* ============================================ */}
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 3, mb: 3 }}>
            {t(`${p}.h2OptionA`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t(`${p}.optionAIntro`)} <RouterLink to={`/${lng}/blog/tfsa-vs-rrsp-vs-fhsa-inmigrantes`} style={{ color: '#1E3A5F', textDecoration: 'underline' }}>{t(`${p}.optionALink`)}</RouterLink>.
          </Typography>

          <Typography variant="body1" component="h3" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 3, mb: 1 }}>
            Tangerine
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }} dangerouslySetInnerHTML={{ __html: t(`${p}.tangerineDesc`) }} />
          <Box sx={{ mt: 2, mb: 3 }}>
            <ReferralCTA bank="tangerine">{t(`${p}.openTangerine`)}</ReferralCTA>
          </Box>

          <Typography variant="body1" component="h3" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 3, mb: 1 }}>
            Simplii Financial
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 1 }}>
            {t(`${p}.simpliiDesc`)}
          </Typography>

          {/* Warning about Quebec */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              bgcolor: '#FFF5F5',
              border: '1px solid #F5B8B8',
              borderRadius: '10px',
              p: 3,
              mt: 3,
              mb: 3,
            }}
          >
            <WarningAmber sx={{ color: '#C62828', mt: 0.3, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: t(`${p}.simpliiQuebecWarning`) }} />
          </Box>

          <Typography variant="body1" component="h3" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 3, mb: 1 }}>
            EQ Bank
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 3 }} dangerouslySetInnerHTML={{ __html: t(`${p}.eqBankDesc`) }} />

          <Typography variant="body1" component="h3" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 3, mb: 1 }}>
            Wealthsimple
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }} dangerouslySetInnerHTML={{ __html: t(`${p}.wealthsimpleDesc`) }} />
          <Box sx={{ mt: 2, mb: 3 }}>
            <ReferralCTA bank="wealthsimple">{t(`${p}.openWealthsimple`)}</ReferralCTA>
          </Box>

          {/* Tabla comparativa - Bancos digitales */}
          <ArticleTable
            columns={[
              { key: 'banco', label: t(`${p}.colBank`) },
              { key: 'fee', label: t(`${p}.colMonthlyFee`) },
              { key: 'eTransfers', label: 'e-Transfers' },
              { key: 'interes', label: t(`${p}.colInterest`) },
              { key: 'quebec', label: t(`${p}.colQuebec`) },
            ]}
            rows={BANCOS_DIGITALES.map((row) => ({
              banco: row.banco,
              fee: { text: row.fee, sx: { color: '#2E7D32', fontWeight: 600 } },
              eTransfers: row.eTransfers,
              interes: row.interes,
              quebec: { text: row.quebec, sx: { color: row.quebec === t(`${p}.no`) ? '#C62828' : '#2E7D32', fontWeight: 600 } },
            }))}
          />

          {/* Info box */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              bgcolor: '#F0FAF1',
              border: '1px solid #B8E0BA',
              borderRadius: '10px',
              p: 3,
              mt: 3,
              mb: 3,
            }}
          >
            <CheckCircleOutline sx={{ color: '#2E7D32', mt: 0.3, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: t(`${p}.digitalAdvantage`) }} />
          </Box>

          <Divider sx={{ mt: 5, mb: 0 }} />

          {/* ============================================ */}
          {/* H2: Opción B: Paquetes de newcomer */}
          {/* ============================================ */}
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 3, mb: 3 }}>
            {t(`${p}.h2OptionB`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }} dangerouslySetInnerHTML={{ __html: t(`${p}.optionBP1`) }} />
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t(`${p}.optionBP2`)} <RouterLink to={`/${lng}/blog/tarjetas-newcomers-que-piden-bancos`} style={{ color: '#1E3A5F', textDecoration: 'underline' }}>{t(`${p}.optionBLink`)}</RouterLink>.
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 3 }}>
            <strong>{t(`${p}.optionBDetailLabel`)}</strong> {t(`${p}.optionBP3`)} <RouterLink to={`/${lng}/blog/construir-credito-canada-desde-cero`} style={{ color: '#1E3A5F', textDecoration: 'underline' }}>{t(`${p}.optionBLink2`)}</RouterLink>.
          </Typography>

          {/* Tabla comparativa - Paquetes newcomer */}
          <ArticleTable
            columns={[
              { key: 'banco', label: t(`${p}.colBank`) },
              { key: 'periodo', label: t(`${p}.colFreePeriod`) },
              { key: 'feeDespues', label: t(`${p}.colFeeAfter`) },
              { key: 'sucursales', label: t(`${p}.colBranches`) },
              { key: 'bono', label: t(`${p}.colWelcomeBonus`) },
            ]}
            rows={NEWCOMER_PAQUETES.map((row) => ({
              banco: row.banco,
              periodo: { text: row.periodo, sx: { color: '#2E7D32', fontWeight: 600 } },
              feeDespues: { text: row.feeDespues, sx: { color: '#C62828', fontWeight: 500 } },
              sucursales: row.sucursales,
              bono: row.bono,
            }))}
          />

          {/* Tip box */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              bgcolor: '#F7F9FC',
              border: '1px solid #B0D4F9',
              borderRadius: '10px',
              p: 3,
              mt: 3,
              mb: 3,
            }}
          >
            <InfoOutlined sx={{ color: '#1E3A5F', mt: 0.3, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: t(`${p}.strategyTip`) }} />
          </Box>

          {/* Mid-article CTA */}
          <Box
            sx={{
              bgcolor: '#F7F9FC',
              border: '1px solid #E6E9EE',
              borderRadius: '12px',
              p: 3,
              mt: 4,
              mb: 2,
              textAlign: 'center',
            }}
          >
            <Typography sx={{ fontSize: '16px', color: '#1A1A1A', mb: 2, lineHeight: 1.6 }}>
              {t(`${p}.midCtaText`)}
            </Typography>
            <Button
              component={RouterLink}
              to={`/${lng}/comparar-cuentas`}
              variant="contained"
              endIcon={<ArrowForward />}
              sx={{
                bgcolor: '#1E3A5F',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '10px',
                px: 3,
                py: 1,
                '&:hover': { bgcolor: '#1A3152' },
              }}
            >
              {t(`${p}.midCtaButton`)}
            </Button>
          </Box>

          <Divider sx={{ mt: 5, mb: 0 }} />

          {/* ============================================ */}
          {/* H2: Opción C: Cuenta low-cost */}
          {/* ============================================ */}
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 3, mb: 3 }}>
            {t(`${p}.h2OptionC`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }} dangerouslySetInnerHTML={{ __html: t(`${p}.optionCP1`) }} />
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }} dangerouslySetInnerHTML={{ __html: t(`${p}.optionCP2`) }} />
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t(`${p}.optionCP3`)}
          </Typography>

          {/* Info box */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              bgcolor: '#F7F9FC',
              border: '1px solid #B0D4F9',
              borderRadius: '10px',
              p: 3,
              mt: 3,
              mb: 3,
            }}
          >
            <InfoOutlined sx={{ color: '#1E3A5F', mt: 0.3, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: t(`${p}.lowCostTip`) }} />
          </Box>

          <Divider sx={{ mt: 5, mb: 0 }} />

          {/* ============================================ */}
          {/* H2: Checklist de onboarding */}
          {/* ============================================ */}
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 3, mb: 3 }}>
            {t(`${p}.h2Checklist`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 3 }}>
            {t(`${p}.checklistIntro`)}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 4 }}>
            {CHECKLIST_STEPS.map((item) => (
              <Box
                key={item.step}
                sx={{
                  display: 'flex',
                  gap: 2.5,
                  bgcolor: '#fff',
                  border: '1px solid #EEF0F3',
                  borderRadius: '10px',
                  p: 3,
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: '0 2px 12px rgba(0,0,0,0.06)' },
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    bgcolor: '#2E7D32',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mt: 0.3,
                  }}
                >
                  <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem' }}>
                    {item.step}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 0.5 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.6 }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <Divider sx={{ mt: 5, mb: 0 }} />

          {/* ============================================ */}
          {/* H2: Red flags: fees escondidos */}
          {/* ============================================ */}
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 3, mb: 3 }}>
            {t(`${p}.h2RedFlags`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 3 }}>
            {t(`${p}.redFlagsIntro`)} <RouterLink to={`/${lng}/blog/costo-de-vida-montreal-2026`} style={{ color: '#1E3A5F', textDecoration: 'underline' }}>{t(`${p}.redFlagsLink`)}</RouterLink> {t(`${p}.redFlagsIntro2`)}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 5 }}>
            {RED_FLAGS.map((flag, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  gap: 2,
                  bgcolor: '#FFFBF0',
                  border: '1px solid #FFE8A0',
                  borderRadius: '10px',
                  p: 3,
                }}
              >
                <WarningAmber sx={{ color: '#F9A825', mt: 0.3, flexShrink: 0 }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 0.5 }}>
                    {flag.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.6 }}>
                    {flag.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <Divider sx={{ mt: 5, mb: 0 }} />

          {/* FAQ Section */}
          <FAQSection faqs={FAQS} />


          {/* Interlink CTA Box */}
          <Box
            sx={{
              bgcolor: '#2E7D32',
              borderRadius: '10px',
              p: 3,
              mt: 4,
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="body1" sx={{ color: '#fff', fontWeight: 600, mb: 0.5 }}>
                {t(`${p}.ctaCreditTitle`)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
                {t(`${p}.ctaCreditDesc`)}
              </Typography>
            </Box>
            <Button
              component={RouterLink}
              to={`/${lng}/tarjetas-de-credito`}
              variant="contained"
              endIcon={<ArrowForward />}
              sx={{
                bgcolor: '#fff',
                color: '#2E7D32',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '14px',
                px: 3,
                flexShrink: 0,
                '&:hover': { bgcolor: '#E0F2F1' },
              }}
            >
              {t(`${p}.ctaCreditButton`)}
            </Button>
          </Box>

          {/* Second CTA - Comparar bancos */}
          <Box
            sx={{
              bgcolor: '#1E3A5F',
              borderRadius: '10px',
              p: 3,
              mt: 2,
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="body1" sx={{ color: '#fff', fontWeight: 600, mb: 0.5 }}>
                {t(`${p}.ctaBanksTitle`)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
                {t(`${p}.ctaBanksDesc`)}
              </Typography>
            </Box>
            <Button
              component={RouterLink}
              to={`/${lng}/comparar/mejores-bancos-recien-llegados-canada-2026`}
              variant="contained"
              endIcon={<ArrowForward />}
              sx={{
                bgcolor: '#fff',
                color: '#1E3A5F',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '14px',
                px: 3,
                flexShrink: 0,
                '&:hover': { bgcolor: '#E8EAF6' },
              }}
            >
              {t(`${p}.ctaBanksButton`)}
            </Button>
          </Box>

          {/* Official Sources */}
          <Box sx={{ mt: 5, pt: 3, borderTop: '1px solid #E6E9EE' }}>
            <Typography variant="subtitle2" sx={{ color: '#5F6B7A', fontWeight: 600, mb: 1.5 }}>
              {t(`${p}.officialSources`)}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Link
                href="https://www.canada.ca/en/financial-consumer-agency/services/banking.html"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: '#1E3A5F', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                {t(`${p}.sourceFCAC`)} <OpenInNew sx={{ fontSize: 14 }} />
              </Link>
              <Link
                href="https://www.cdic.ca"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: '#1E3A5F', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                {t(`${p}.sourceCDIC`)} <OpenInNew sx={{ fontSize: 14 }} />
              </Link>
              <Link
                href="https://www.canada.ca/en/financial-consumer-agency/services/banking/low-cost-accounts.html"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: '#1E3A5F', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                {t(`${p}.sourceLowCost`)} <OpenInNew sx={{ fontSize: 14 }} />
              </Link>
            </Box>
            <Typography variant="caption" sx={{ color: '#5F6B7A', display: 'block', mt: 2 }}>
              {t(`${p}.updatedDate`)}
            </Typography>
          </Box>

          {/* Author Bio */}
          <Divider sx={{ mt: 4, mb: 3 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, p: 3, bgcolor: '#F7F9FC', borderRadius: '12px' }}>
            <Box component="img" src={AUTHOR.photo} alt={AUTHOR.name} loading="lazy" sx={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1A1A1A', lineHeight: 1.3 }}>{AUTHOR.name}</Typography>
              <Typography variant="body2" sx={{ color: '#5F6B7A' }}>{AUTHOR.role}</Typography>
            </Box>
            <Button href={AUTHOR.linkedin} target="_blank" rel="noopener noreferrer" startIcon={<LinkedIn />} size="small"
              sx={{ textTransform: 'none', color: '#1E3A5F', fontWeight: 600, borderColor: '#1E3A5F', '&:hover': { bgcolor: 'rgba(10,102,194,0.08)' } }}
              variant="outlined">
              LinkedIn
            </Button>
          </Box>

        </Box>

        <RelatedArticles slug="mejores-cuentas-sin-fees-recien-llegados" />
      </Container>

      <NewsletterSection />
    </Box>
  );
};

export default CuentasSinFeesPage;
